import { apiService } from "@/services";
import { ApiError } from "@/services/api.service";
import { toast } from "sonner";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  UseQueryOptions,
  type QueryKey,
} from "@tanstack/react-query";
import {
  BulkCreateHeroProductsParams,
  GetProductsParams,
  UpdateMasterListParams,
  Product,
  ProductLifecycleState,
  ProductListItem,
  UtilizationTag,
  HeroKeywordsQueryParams,
  Keyword,
  SimpleSourceTag,
  ABTestType, ContentType, HeroAsinWithIntent, UploadHeroAbTestResultsResponse,
  AiValidationResult, CreateHeroAbTestResponse, UpdateHeroAbTestResponse,
} from "@/types";
import queryClient from "@/query-client";
import { brandSignalsQueryKey } from "@/resources/brand-signals";
import { keywordThemesQueryKey } from "@/resources/keyword-theme";
import { getSeoStrategyRecommendationsQueryPrefix } from "@/resources/seo-strategy/seo-strategy.api";
import { handleApiError } from "@/utils";
import { DEFAULT_STALE_TIME, KeywordMasterListAction } from "@/constants";

type BulkUpdateMasterListParams = {
  productId: number;
  keywordIds: number[];
  action: KeywordMasterListAction;
};

type MasterListMutationContext = {
  snapshots: Array<[readonly unknown[], Keyword[] | undefined]>;
  landscapeSnapshots: Array<[readonly unknown[], unknown]>;
};

const applyLandscapeMasterListPatch = (
  current: unknown,
  keywordIds: Set<number>,
  isMasterList: boolean,
): unknown => {
  if (!current || typeof current !== "object") return current;
  const data = current as { keywords?: Array<{ id: string; is_master_list?: boolean }> };
  if (!Array.isArray(data.keywords)) return current;
  let changed = false;
  const nextKeywords = data.keywords?.map((kw) => {
    if (!keywordIds.has(Number(kw.id))) return kw;
    if (kw.is_master_list === isMasterList) return kw;
    changed = true;
    return { ...kw, is_master_list: isMasterList };
  });
  if (!changed) return current;
  return { ...data, keywords: nextKeywords };
};

const getKeywordsQueryPrefix = (productId: number) =>
  ["product", "keywords", String(productId)] as const;

const applyMasterListOptimisticPatch = (
  current: Keyword[] | undefined,
  keywordIds: Set<number>,
  isMasterList: boolean,
): Keyword[] | undefined => {
  if (!current) return current;

  let changed = false;
  const nextData = current?.map((keyword) => {
    if (!keywordIds.has(keyword.id)) return keyword;
    if (keyword.is_master_list === isMasterList) return keyword;
    changed = true;
    return {
      ...keyword,
      is_master_list: isMasterList,
    };
  });

  if (!changed) return current;
  return nextData;
};

export const useGetProductBrand = (productId?: string) => {
  return useQuery({
    queryKey: ["product", "brand", productId],
    queryFn: () => apiService.get<{ brand_id: number }>(`/products/${productId}/brand`),
    enabled: !!productId,
    staleTime: Infinity,
  });
};

export const useGetHeroProductList = (brandId?: number, options?: object) => {
  return useQuery({
    queryKey: ["hero", "product", "list", brandId],
    queryFn: () =>
      apiService.get<ProductListItem[], GetProductsParams>(`/products`, {
        brand_id: brandId,
        hero: true,
      }),
    enabled: !!brandId,
    staleTime: DEFAULT_STALE_TIME,
    ...options,
  });
};

export const useGetAllProductList = (brandId?: number) => {
  return useQuery({
    queryKey: ["all", "product", "list", brandId],
    queryFn: () =>
      apiService.get<ProductListItem[], GetProductsParams>(`/products`, {
        brand_id: brandId,
        hero: false,
      }),
    enabled: !!brandId,
    staleTime: DEFAULT_STALE_TIME,
  });
};

export interface AddHeroProductsResult {
  added: number;
  already_added: number;
}

// AUT-175: toast copy for the add-hero-ASINs result -- how many ASINs were newly
// added vs already present (mirrors the keyword bulk-add feedback).
export const describeAddHeroProductsResult = ({ added, already_added }: AddHeroProductsResult) => {
  if (added === 0) {
    return {
      title: "Already added",
      description: `${already_added} ASIN${already_added !== 1 ? "s" : ""} ${already_added === 1 ? "is" : "are"} already in your products.`,
    };
  }
  const dup = already_added > 0 ? `, ${already_added} already added` : "";
  return {
    title: "Hero ASIN(s) Added",
    description: `${added} ASIN${added !== 1 ? "s" : ""} added${dup}.`,
  };
};

export const useAddHeroProducts = () => {
  return useMutation({
    mutationFn: ({ brandId, asins }: { brandId: number; asins: string[] }) =>
      apiService.post<AddHeroProductsResult, BulkCreateHeroProductsParams>(`/products`, { brand_id: brandId, asins }),
    onSuccess: (_, { brandId }) => {
      queryClient.refetchQueries({
        queryKey: ["hero", "product", "list", brandId],
      });
      queryClient.invalidateQueries({
        queryKey: ["all", "product", "list", brandId],
      });
    },
  });
};

type HeroProductDetailQueryOptions = Omit<
  UseQueryOptions<Product, Error, Product>,
  "queryKey" | "queryFn" | "select"
> & {
  includeKeywords?: boolean;
};

export const useGetHeroProductDetail = (
  productId?: string,
  brandId?: number,
  options?: HeroProductDetailQueryOptions,
) => {
  const { includeKeywords = true, ...queryOptions } = options ?? {};
  return useQuery({
    queryKey: ["product", "detail", productId, brandId, includeKeywords],
    queryFn: () =>
      apiService.get<Product, { brand_id: number; include_keywords: 0 | 1 }>(`/products/hero/${productId}`, {
        brand_id: brandId,
        include_keywords: includeKeywords ? 1 : 0,
      }),
    enabled: !!productId && !!brandId,
    staleTime: DEFAULT_STALE_TIME,
    select: (data) => ({
      ...data,
      keywords: (data.keywords ?? []).map((keyword) => ({
        ...keyword,
        utilizationTags: (keyword.utilization ?? []) as UtilizationTag[],
        sourceTags: (keyword.sources ?? []) as SimpleSourceTag[],
      })),
    }),
    ...queryOptions,
  });
};

export const useGetHeroProductKeywords = (
  productId: string | undefined,
  brandId: number | null | undefined,
  options?: object,
) => {
  const canFetch =
    !!productId && brandId != null;
  const queryParams: HeroKeywordsQueryParams = {
    brand_id: brandId,
  };
  return useQuery({
    queryKey: ["product", "keywords", productId, brandId],
    queryFn: () =>
      apiService.get<Keyword[], HeroKeywordsQueryParams>(
        `/products/hero/${productId}/keywords`,
        queryParams,
      ),
    enabled: canFetch,
    placeholderData: keepPreviousData,
    staleTime: DEFAULT_STALE_TIME,
    ...options,
  });
};

export const fetchHeroProductKeywords = (
  productId: string,
  brandId: number,
): Promise<Keyword[]> =>
  queryClient.fetchQuery({
    queryKey: ["product", "keywords", productId, brandId],
    queryFn: () =>
      apiService.get<Keyword[], { brand_id: number }>(
        `/products/hero/${productId}/keywords`,
        { brand_id: brandId },
      ),
    staleTime: DEFAULT_STALE_TIME,
  });

export const useGetHeroProductCompetitors = (productId?: string, brandId?: number, options?: object) => {
  return useQuery({
    queryKey: ["product", "competitors", productId, brandId],
    queryFn: () => apiService.get<ProductListItem[], { brand_id: number }>(`/products/hero/${productId}/competitors`, { brand_id: brandId! }),
    enabled: !!productId && !!brandId,
    staleTime: DEFAULT_STALE_TIME,
    ...options,
  });
};

export const useGetHeroProductABTests = (
  productId?: string,
  brandId?: number,
  options?: object,
) => {
  const enabled = !!productId && brandId !== null;

  return useQuery({
    queryKey: ["product", "ab-tests", productId, brandId],
    queryFn: () =>
      apiService.get<HeroAsinWithIntent, { brand_id: number }>(
        `/products/hero/${productId}/ab-tests`,
        { brand_id: brandId },
      ),
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    ...options,
  });
};

/** All hero products’ A/B payloads for the current brand — single aggregate request. */
export const useGetBrandHeroAbTests = (brandId: number | null | undefined) => {
  const query = useQuery({
    queryKey: ["brand", "ab-tests", brandId],
    queryFn: () =>
      apiService.get<HeroAsinWithIntent[], undefined>(`/brands/${brandId}/ab-tests`),
    enabled: !!brandId,
    staleTime: DEFAULT_STALE_TIME,
  });

  return {
    data: query.data ?? [],
    isPending: query.isPending,
    heroList: [] as ProductListItem[],
  };
};

export type { UploadHeroAbTestResultsResponse };

type GenerateAbTestCopyParams = {
  productId: number;
  brandId: number;
  intentFrom?: string;
  intentTo?: string;
  targetKeyword?: string;
  hypothesis: string;
  selectedFields: string[];
  variantATitle?: string;
  variantABullets?: string[];
};

export type GenerateAbTestCopyResponse = {
  title: string | null;
  bulletsByField: Record<string, string>;
  structural_rationale?: string | null;
  validation?: AiValidationResult;
};

export const useGenerateHeroAbTestCopy = () => {
  return useMutation({
    mutationFn: (params: GenerateAbTestCopyParams) =>
      apiService.post<GenerateAbTestCopyResponse, {
        brand_id: number;
        intent_from?: string;
        intent_to?: string;
        target_keyword?: string;
        hypothesis: string;
        selected_fields: string[];
      }>(`/products/hero/${params.productId}/ab-tests/generate-variant`, {
        brand_id: params.brandId,
        intent_from: params.intentFrom,
        intent_to: params.intentTo,
        target_keyword: params.targetKeyword,
        hypothesis: params.hypothesis,
        selected_fields: params.selectedFields,
        variant_a_title: params.variantATitle,
        variant_a_bullets: params.variantABullets,
      }),
  });
};

type GenerateAbTestHypothesisParams = {
  productId: number;
  brandId: number;
  testType: ABTestType;
  intentFrom?: string;
  intentTo?: string;
  targetKeyword?: string;
  selectedFields: string[];
  axisDeltas?: Record<string, number>;
};

export const useGenerateHeroAbTestHypothesis = () => {
  return useMutation({
    mutationFn: (params: GenerateAbTestHypothesisParams) =>
      apiService.post<{ hypothesis: string; validation?: AiValidationResult }, {
        brand_id: number;
        test_type: string;
        intent_from?: string;
        intent_to?: string;
        target_keyword?: string;
        selected_fields: string[];
        axis_deltas?: Record<string, number>;
      }>(`/products/hero/${params.productId}/ab-tests/generate-hypothesis`, {
        brand_id: params.brandId,
        test_type: params.testType,
        intent_from: params.intentFrom,
        intent_to: params.intentTo,
        target_keyword: params.targetKeyword,
        selected_fields: params.selectedFields,
        axis_deltas: params.axisDeltas,
      }),
  });
};

export type CreateHeroAbTestBody = {
  brand_id: number;
  test_type: ABTestType;
  scope_fields: ContentType[];
  content_type: ContentType;
  intent_from: string;
  intent_to: string;
  hypothesis: string;
  target_keyword?: string;
  target_phrase?: string;
  variant_a?: {
    title?: string;
    bullets: string[];
  };
  variant_b: {
    title?: string;
    bullets: string[];
  };
  status?: "draft" | "building" | "planning" | "running" | "paused" | "completed" | "archived";
  source?: string;
  source_detail?: string;
  /**
   * AUT-223: links the created test back to an SEO Strategy recommendation.
   * AUT-430 (WEB-008): widened to admit `null` explicitly (the wire value for "no
   * link") alongside the existing absent-key case -- both still store `null`.
   */
  seo_strategy_recommendation_id?: number | null;
  keyword_to_remove?: string;
  custom_axes?: string[];
};

type CreateHeroAbTestParams = {
  productId: number;
  body: CreateHeroAbTestBody;
};

export const useCreateHeroAbTest = () => {
  return useMutation({
    mutationFn: (params: CreateHeroAbTestParams) =>
      apiService.post<CreateHeroAbTestResponse, CreateHeroAbTestBody>(
        `/products/hero/${params.productId}/ab-tests`,
        params.body,
      ),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["product", "ab-tests", String(variables.productId), variables.body.brand_id],
      });
      queryClient.invalidateQueries({
        queryKey: ["brand", "ab-tests", variables.body.brand_id],
      });
      queryClient.invalidateQueries({ queryKey: brandSignalsQueryKey(variables.body.brand_id) });
      queryClient.invalidateQueries({ queryKey: ["product", "keywords"] });
      // AUT-430 (WEB-013): a created test can appear on a strategy's card immediately.
      // Not awaited, same as the invalidations above.
      queryClient.invalidateQueries({ queryKey: getSeoStrategyRecommendationsQueryPrefix() });
    },
  });
};

export type UpdateHeroAbTestBody = {
  brand_id: number;
  scope_fields?: ContentType[];
  content_type?: ContentType;
  intent_from?: string;
  intent_to?: string;
  hypothesis?: string;
  target_keyword?: string;
  target_cluster?: string;
  target_phrase?: string;
  variant_a?: {
    title?: string;
    bullets: string[];
  };
  variant_b?: {
    title?: string;
    bullets: string[];
  };
  outcome?: "control" | "variant" | "inconclusive";
  variant_b_switch_date?: string;
  source?: string;
  source_detail?: string;
  keyword_to_remove?: string;
  status?: string;
  /**
   * AUT-430 (WEB-008/FEATURE-001): three-valued on PATCH -- absent leaves the
   * stored link unchanged, a number checks and sets it, `null` clears it. The
   * key must be omitted entirely for "leave unchanged" (never sent as
   * `undefined`), which is why callers build this object conditionally rather
   * than always assigning the field. `decisions/a-strategy-link-is-checked-
   * against-the-tests-hero-product-at-write.md`.
   */
  seo_strategy_recommendation_id?: number | null;
};

export type HeroAbTestUpdatePayload = Omit<UpdateHeroAbTestBody, "brand_id">;

type UpdateHeroAbTestParams = {
  productId: number;
  testId: string;
  body: UpdateHeroAbTestBody;
};

export const useUpdateHeroAbTest = () => {
  return useMutation({
    mutationFn: (params: UpdateHeroAbTestParams) =>
      apiService.patch<UpdateHeroAbTestResponse, UpdateHeroAbTestBody>(
        `/products/hero/${params.productId}/ab-tests/${params.testId}`,
        params.body,
      ),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["product", "ab-tests", String(variables.productId), variables.body.brand_id],
      });
      queryClient.invalidateQueries({
        queryKey: ["brand", "ab-tests", variables.body.brand_id],
      });
      queryClient.invalidateQueries({ queryKey: brandSignalsQueryKey(variables.body.brand_id) });
      queryClient.invalidateQueries({ queryKey: ["product", "keywords"] });
      // AUT-430 (WEB-013): link set/changed/cleared, name/status/outcome/keyword changed, or
      // syncFromTest completed the strategy -- every case can change the strategy list.
      queryClient.invalidateQueries({ queryKey: getSeoStrategyRecommendationsQueryPrefix() });
    },
  });
};

type DeleteHeroAbTestParams = {
  productId: number;
  testId: string;
  brandId: number;
};

export const useDeleteHeroAbTest = () => {
  return useMutation({
    mutationFn: (params: DeleteHeroAbTestParams) =>
      apiService.delete<{ message: string }, unknown>(
        `/products/hero/${params.productId}/ab-tests/${params.testId}`,
        {},
        { params: { brand_id: params.brandId } },
      ),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["product", "ab-tests", String(variables.productId), variables.brandId],
      });
      queryClient.invalidateQueries({
        queryKey: ["brand", "ab-tests", variables.brandId],
      });
      queryClient.invalidateQueries({ queryKey: brandSignalsQueryKey(variables.brandId) });
      queryClient.invalidateQueries({ queryKey: ["product", "keywords"] });
      // AUT-430 (WEB-013): a linked strategy's card loses this test.
      queryClient.invalidateQueries({ queryKey: getSeoStrategyRecommendationsQueryPrefix() });
    },
  });
};

type UploadHeroAbTestResultsParams = {
  productId: number;
  testId: string;
  brandId: number;
  file: File;
  outcome?: string;
  variantBSwitchDate?: string;
};

export const useUploadHeroAbTestResults = () => {
  return useMutation({
    mutationFn: async (params: UploadHeroAbTestResultsParams) => {
      const formData = new FormData();
      formData.append("brand_id", String(params.brandId));
      formData.append("file", params.file);
      if (params.outcome) formData.append("outcome", params.outcome);
      if (params.variantBSwitchDate) formData.append("variant_b_switch_date", params.variantBSwitchDate);
      return apiService.post<UploadHeroAbTestResultsResponse, FormData>(
        `/products/hero/${params.productId}/ab-tests/${params.testId}/upload-results`,
        formData,
      );
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["product", "ab-tests", String(variables.productId), variables.brandId],
      });
      queryClient.invalidateQueries({
        queryKey: ["brand", "ab-tests", variables.brandId],
      });
      queryClient.invalidateQueries({ queryKey: brandSignalsQueryKey(variables.brandId) });
      // AUT-430 (WEB-013): the linked strategy's card shows the test's new outcome.
      queryClient.invalidateQueries({ queryKey: getSeoStrategyRecommendationsQueryPrefix() });
    },
  });
};

export const useDeleteHeroProduct = () => {
  return useMutation<
    { message: string },
    Error,
    { productId: number; brandId: number },
    { previousProducts: ProductListItem[] | undefined }
  >({
    mutationFn: ({ productId, brandId }) =>
      apiService.delete<{ message: string }, unknown>(
        `/products/hero/${productId}`,
        {},
        { params: { brand_id: brandId } },
      ),
    onMutate: async (variables) => {
      toast.loading("Deleting product...", { id: `delete-product-${variables.productId}` });

      const listQueryKey = ["hero", "product", "list", variables.brandId] as const;
      await queryClient.cancelQueries({ queryKey: listQueryKey });

      const previousProducts = queryClient.getQueryData<ProductListItem[]>(listQueryKey);

      queryClient.setQueryData<ProductListItem[]>(listQueryKey, (old) =>
        old ? old.filter((p) => p.id !== variables.productId) : old,
      );

      return { previousProducts };
    },
    onSuccess: (response, variables) => {
      toast.success(
        (response as { message?: string })?.message || "Product deleted successfully",
        { id: `delete-product-${variables.productId}` },
      );
      queryClient.invalidateQueries({ queryKey: ["hero", "product", "list", variables.brandId] });
      queryClient.invalidateQueries({ queryKey: ["all", "product", "list", variables.brandId] });
      queryClient.invalidateQueries({ queryKey: ["product"] });
      queryClient.invalidateQueries({ queryKey: ["brand", "ab-tests", variables.brandId] });
      queryClient.invalidateQueries({ queryKey: ["landscapes"] });
      queryClient.invalidateQueries({ queryKey: ["seo-insights"] });
      queryClient.invalidateQueries({ queryKey: ["scorecards"] });
      queryClient.invalidateQueries({ queryKey: ["search-term-sets"] });
      queryClient.invalidateQueries({ queryKey: ["rank-tracker"] });
      queryClient.invalidateQueries({ queryKey: brandSignalsQueryKey(variables.brandId) });
    },
    onError: (error, variables, context) => {
      if (context?.previousProducts) {
        queryClient.setQueryData<ProductListItem[]>(
          ["hero", "product", "list", variables.brandId],
          context.previousProducts,
        );
      }

      let errorMessage = "Failed to delete product. Please try again.";
      if (error instanceof ApiError) {
        const data = error.data as { message?: string } | undefined;
        if (data?.message) errorMessage = data.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast.error(errorMessage, { id: `delete-product-${variables.productId}` });
    },
  });
};

export const useUpdateMasterList = () => {
  return useMutation<void, unknown, UpdateMasterListParams, MasterListMutationContext>({
    mutationFn: (data) =>
      apiService.patch<void, { is_master_list: boolean }>(
        `/products/${data.productId}/keywords/${data.keywordId}/master-list`,
        { is_master_list: data.action === KeywordMasterListAction.ADD },
      ),
    onMutate: async (variables): Promise<MasterListMutationContext> => {
      const queryPrefix = getKeywordsQueryPrefix(variables.productId);
      await queryClient.cancelQueries({ queryKey: queryPrefix });
      await queryClient.cancelQueries({ queryKey: ["landscape-detail"] });

      const snapshots = queryClient.getQueriesData<Keyword[]>({
        queryKey: queryPrefix,
      });
      const landscapeSnapshots = queryClient.getQueriesData({
        queryKey: ["landscape-detail"],
      });

      const keywordIds = new Set([variables.keywordId]);
      const nextFlag = variables.action === KeywordMasterListAction.ADD;
      for (const [queryKey, prev] of snapshots) {
        queryClient.setQueryData<Keyword[] | undefined>(
          queryKey,
          applyMasterListOptimisticPatch(prev, keywordIds, nextFlag),
        );
      }
      for (const [queryKey, prev] of landscapeSnapshots) {
        queryClient.setQueryData(
          queryKey,
          applyLandscapeMasterListPatch(prev, keywordIds, nextFlag),
        );
      }

      return { snapshots, landscapeSnapshots };
    },
    onError: (_error, variables, context) => {
      if (!context) return;
      for (const [queryKey, snapshot] of context.snapshots) {
        queryClient.setQueryData(queryKey, snapshot);
      }
      for (const [queryKey, snapshot] of context.landscapeSnapshots) {
        queryClient.setQueryData(queryKey, snapshot);
      }
      queryClient.invalidateQueries({ queryKey: ["product", "detail"] });
      queryClient.invalidateQueries({ queryKey: ["product", "keywords", String(variables.productId)] });
      handleApiError(_error);
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: ["product", "detail"] });
      queryClient.invalidateQueries({ queryKey: ["product", "keywords", String(variables.productId)] });
      // Master List membership belongs to the phrase, and the server now flags every
      // `landscape_keywords` row that shares it (`Support\MasterListMembership`). The optimistic
      // patch above can only match rows by id, and a landscape's row for the same phrase has a
      // different one — so a landscape already in cache would keep the stale star. Refetch it.
      queryClient.invalidateQueries({ queryKey: ["landscape-detail"] });
      if (variables.action === KeywordMasterListAction.REMOVE) {
        queryClient.invalidateQueries({ queryKey: keywordThemesQueryKey(variables.productId) });
      }
      if (variables.action === KeywordMasterListAction.ADD) {
        queryClient.invalidateQueries({ queryKey: ["rank-tracker", "hero-products"] });
        queryClient.invalidateQueries({ queryKey: ["rank-tracker", "keywords"] });
        queryClient.invalidateQueries({ queryKey: ["rank-tracker", "brand-summary"] });
        queryClient.invalidateQueries({ queryKey: ["rank-tracker", "all-keywords"] });
      }
    },
  });
};

type BulkUpdateMasterListResponse = {
  updated_count: number;
};

export const useBulkUpdateMasterList = () => {
  return useMutation<BulkUpdateMasterListResponse, unknown, BulkUpdateMasterListParams, MasterListMutationContext>({
    mutationFn: (data) =>
      apiService.patch<BulkUpdateMasterListResponse, { keyword_ids: number[]; is_master_list: boolean }>(
        `/products/${data.productId}/keywords/master-list`,
        {
          keyword_ids: data.keywordIds,
          is_master_list: data.action === KeywordMasterListAction.ADD,
        },
      ),
    onMutate: async (variables): Promise<MasterListMutationContext> => {
      const queryPrefix = getKeywordsQueryPrefix(variables.productId);
      await queryClient.cancelQueries({ queryKey: queryPrefix });
      await queryClient.cancelQueries({ queryKey: ["landscape-detail"] });

      const snapshots = queryClient.getQueriesData<Keyword[]>({
        queryKey: queryPrefix,
      });
      const landscapeSnapshots = queryClient.getQueriesData({
        queryKey: ["landscape-detail"],
      });

      const keywordIds = new Set(variables.keywordIds);
      const nextFlag = variables.action === KeywordMasterListAction.ADD;
      for (const [queryKey, prev] of snapshots) {
        queryClient.setQueryData<Keyword[] | undefined>(
          queryKey,
          applyMasterListOptimisticPatch(prev, keywordIds, nextFlag),
        );
      }
      for (const [queryKey, prev] of landscapeSnapshots) {
        queryClient.setQueryData(
          queryKey,
          applyLandscapeMasterListPatch(prev, keywordIds, nextFlag),
        );
      }

      return { snapshots, landscapeSnapshots };
    },
    onError: (_error, _variables, context) => {
      if (!context) return;
      for (const [queryKey, snapshot] of context.snapshots) {
        queryClient.setQueryData(queryKey, snapshot);
      }
      for (const [queryKey, snapshot] of context.landscapeSnapshots) {
        queryClient.setQueryData(queryKey, snapshot);
      }
      queryClient.invalidateQueries({ queryKey: ["product", "detail"] });
      queryClient.invalidateQueries({ queryKey: ["product", "keywords", String(_variables?.productId)] });
      handleApiError(_error);
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: ["product", "detail"] });
      queryClient.invalidateQueries({ queryKey: ["product", "keywords", String(variables.productId)] });
      // Master List membership belongs to the phrase, and the server now flags every
      // `landscape_keywords` row that shares it (`Support\MasterListMembership`). The optimistic
      // patch above can only match rows by id, and a landscape's row for the same phrase has a
      // different one — so a landscape already in cache would keep the stale star. Refetch it.
      queryClient.invalidateQueries({ queryKey: ["landscape-detail"] });
      if (variables.action === KeywordMasterListAction.REMOVE) {
        queryClient.invalidateQueries({ queryKey: keywordThemesQueryKey(variables.productId) });
      }
      if (variables.action === KeywordMasterListAction.ADD) {
        queryClient.invalidateQueries({ queryKey: ["rank-tracker", "hero-products"] });
        queryClient.invalidateQueries({ queryKey: ["rank-tracker", "keywords"] });
        queryClient.invalidateQueries({ queryKey: ["rank-tracker", "brand-summary"] });
        queryClient.invalidateQueries({ queryKey: ["rank-tracker", "all-keywords"] });
      }
    },
  });
};

/**
 * `PATCH /products/hero/{id}/lifecycle-state` — the one write path for a product's lifecycle state
 * (AUT-435 / WEB-002; `components/product.md § The endpoint`).
 *
 * **It patches the cached row and never invalidates the hero product list.**
 * `Products.tsx:63` binds `isFetching` — not `isLoading` — and `:107-108` returns a
 * `<FullScreenLoader />` from it, so invalidating `["hero","product","list",brandId]` on a query
 * that already has data replaces the whole page on every save, taking the dialog, the scroll
 * position, the search box and the filter chips with it. `intent.md § FR-8` forbids exactly that.
 * `decisions/a-lifecycle-save-patches-the-cached-product-row.md` is this hook's specification.
 * (`useDeleteHeroProduct` above invalidates that same key in its `onSuccess`, which is why the
 * delete flow blanks the page today — recorded as
 * `tech-debt.md § hero-delete-invalidation-blanks-the-products-page`, not fixed here.)
 *
 * **No toast and no `handleApiError`.** The success copy needs the product name and fires after the
 * dialog has closed, and a `4xx`/`5xx` must leave the dialog open showing the error — a toast from
 * in here would dismiss a failure the user is still reading. The dialog owns both (`WEB-004`), and
 * the rejection reaches the caller untouched so it can read `ApiError.status` and
 * `ApiError.isNetworkError`.
 *
 * The response shape is typed inline rather than mirrored in `src/types/`:
 * `conventions/08-api-contracts.md` scopes that ban to components, and `useDeleteHeroProduct` types
 * `{ message: string }` inline in this same module.
 */
/**
 * The detail query's key is `["product","detail",productId,brandId,includeKeywords]` with a **string**
 * product id and a trailing flag the host chooses, so the write below matches it by prefix through
 * `setQueriesData` -- that covers both `includeKeywords` variants without the mutation having to know
 * which one is mounted. Writing the raw cached row is correct even though the query carries a
 * `select`: `select` runs on read, so it re-derives from the patched value.
 */
const heroProductDetailQueryPrefix = (productId: number, brandId: number) =>
  ["product", "detail", String(productId), brandId] as const;

export const useSetProductLifecycleState = () => {
  return useMutation<
    { id: number; lifecycle_state: ProductLifecycleState },
    Error,
    { productId: number; brandId: number; lifecycleState: ProductLifecycleState },
    {
      previousProducts: ProductListItem[] | undefined;
      previousDetails: [QueryKey, Product | undefined][];
    }
  >({
    mutationFn: ({ productId, brandId, lifecycleState }) =>
      apiService.patch<
        { id: number; lifecycle_state: ProductLifecycleState },
        { brand_id: number; lifecycle_state: ProductLifecycleState }
      >(`/products/hero/${productId}/lifecycle-state`, {
        brand_id: brandId,
        lifecycle_state: lifecycleState,
      }),
    onMutate: async ({ productId, brandId, lifecycleState }) => {
      const listQueryKey = ["hero", "product", "list", brandId] as const;
      await queryClient.cancelQueries({ queryKey: listQueryKey });

      const previousProducts = queryClient.getQueryData<ProductListItem[]>(listQueryKey);

      queryClient.setQueryData<ProductListItem[]>(listQueryKey, (old) =>
        old
          ? old.map((product) =>
              product.id === productId
                ? { ...product, lifecycle_state: lifecycleState }
                : product,
            )
          : old,
      );

      // The product-detail header renders the same badge from its own query, which the list patch
      // above does not reach -- on that page the list is usually not even mounted. Patched in place
      // for the same reason the list is: `ProductDetail.tsx` returns a `<FullScreenLoader />` while
      // its detail query is pending, so an invalidation would blank the whole page mid-save
      // (`decisions/a-lifecycle-save-patches-the-cached-product-row.md`).
      const detailPrefix = heroProductDetailQueryPrefix(productId, brandId);
      await queryClient.cancelQueries({ queryKey: detailPrefix });

      const previousDetails = queryClient.getQueriesData<Product>({ queryKey: detailPrefix });

      queryClient.setQueriesData<Product>({ queryKey: detailPrefix }, (old) =>
        old ? { ...old, lifecycle_state: lifecycleState } : old,
      );

      return { previousProducts, previousDetails };
    },
    onError: (_error, { brandId }, context) => {
      if (context?.previousProducts) {
        queryClient.setQueryData<ProductListItem[]>(
          ["hero", "product", "list", brandId],
          context.previousProducts,
        );
      }

      // Restored key by key rather than by prefix: `getQueriesData` returns the exact keys that were
      // cached, so a variant that did not exist before the save is not resurrected by the rollback.
      context?.previousDetails?.forEach(([queryKey, data]) => {
        queryClient.setQueryData<Product>(queryKey, data);
      });
    },
  });
};
